"use client"

import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, Controller } from "react-hook-form"
import * as z from "zod"
import { cn } from "@/lib/utils"
import { checkoutSubscribeSchema } from "@/lib/validations/subscribe"
import { buttonVariants } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"
import { Icons } from "@/components/icons"
import { Checkbox } from "@/components/ui/checkbox"
import Link from 'next/link'
import { usePostHog } from "posthog-js/react"

interface CheckoutSubscribeFormProps extends React.HTMLAttributes<HTMLDivElement> {
    userId: string;
    hexColour?: string;
    name: string | null;
    title: string;
    slug: string;
    stripePriceId: string | null;
    productId: number;
    setOpen?: (open: boolean) => void;
    fetchClientSecret: (email: string, firstName: string, lastName: string, title: string, userId: string, stripePriceId: string) => Promise<any>;
    onSuccessfulSubmission: () => void;
}

type FormData = z.infer<typeof checkoutSubscribeSchema>

export function CheckoutSubscribeForm({ className, hexColour, name, productId, title, slug, userId, stripePriceId, fetchClientSecret, setOpen, onSuccessfulSubmission, ...props }: CheckoutSubscribeFormProps) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(checkoutSubscribeSchema),
    defaultValues: {
        shareEmailWithUserOptIn: false
    },
  })
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const posthog = usePostHog()

  async function onSubmit(data: FormData) {
    setIsLoading(true)

    const completeData = { ...data, userId: userId, termsAndPrivacyOptIn: true };

    const response = await fetch(`/api/users/subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(completeData),
    })

    if (response.ok) {
        posthog.identify(
            data.email,
            { email: data.email }
        )
        posthog.capture(
            'Subscribed to profile from checkout', 
            {
                subscriber_email: data.email,
                subscribee_user_id: userId,
                subscribee_username: slug,
                subscribee_name: name,
                product_id: productId,
                product_title: title,
            }
        )
    }

    setIsLoading(false)
    if (setOpen) {
        setOpen(false)
    }
    
    if (stripePriceId && userId) {
        const clientSecret = fetchClientSecret(data.email, data.firstName, data.lastName, title, userId, stripePriceId)
        if(!clientSecret) {
            return toast({
                title: 'Something went wrong, sorry! Please try again.',
                variant: "destructive",
            })
        }
    }

    onSuccessfulSubmission();

  }

  return (
    <div className={cn("space-y-2 items-center flex flex-col", className)} {...props}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <div className="">
            <Label htmlFor="email">
              Email
            </Label>
            <Input
              id="email"
              placeholder="name@example.com"
              type="email"
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              disabled={isLoading}
              className="mt-1"
              {...register("email")}
            />
            {errors?.email && (
              <p className="px-1 text-xs text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>
          <div className="">
            <Label htmlFor="first-name">
              First Name
            </Label>
            <Input
              id="first-name"
              placeholder=""
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              disabled={isLoading}
              className="mt-1"
              {...register("firstName")}
            />
            {errors?.firstName && (
              <p className="px-1 text-xs text-red-600">
                {errors.firstName.message}
              </p>
            )}
          </div>
          <div className="">
            <Label htmlFor="last-name">
              Last Name
            </Label>
            <Input
              id="last-name"
              placeholder=""
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              disabled={isLoading}
              className="mt-1"
              {...register("lastName")}
            />
            {errors?.lastName && (
              <p className="px-1 text-xs text-red-600">
                {errors.lastName.message}
              </p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Controller
                control={control}
                name="shareEmailWithUserOptIn"
                render={({ field }) => (
                    <Checkbox
                    id="share-with-user"
                    {...register("shareEmailWithUserOptIn")}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    style={{ backgroundColor: field.value ? hexColour : undefined }}
                    />
                )}
            />
            <label
                htmlFor="share-with-user"
                className="text-xs leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
                I agree to receive useful email updates from {name || slug}.
            </label>
          </div>
            {errors?.shareEmailWithUserOptIn && (
                <div className="my-1">
                <p className="px-1 text-xs text-red-600">
                    {errors.shareEmailWithUserOptIn.message}
                </p>
                </div>
            )}
          <div className="flex flex-col items-center space-y-1">
            <button style={{backgroundColor: hexColour}} className={cn(buttonVariants({variant: "black"}), "text-white font-semibold text-center justify-center w-full items-center bg-black")} disabled={isLoading}>
                {isLoading && (
                <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                )}
                Continue
            </button>
            <p className="text-xs italic text-center">By clicking Continue, you agree to Superstack&#39;s <Link target="_blank" href="/terms" style={{textDecoration: 'none'}}>Terms and Conditions</Link> and <Link target="_blank" href="/privacy" style={{textDecoration: 'none'}}>Privacy Policy</Link></p>
          </div>
        </div>
      </form>
    </div>
  )
}